import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Source as SourceIcon,
  Update as UpdateIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { ValidationResult, getSeverityColor, getSeverityIcon } from '../utils/medicalValidators';
import { sourceManager, SourceReference } from '../utils/medicalSources';
import { medicalVersionManager } from '../utils/medicalVersioning';

interface MedicalInfoProps {
  validationResult?: ValidationResult;
  sourceReferences?: SourceReference[];
  protocolId?: string;
  showDetails?: boolean;
}

const MedicalInfo: React.FC<MedicalInfoProps> = ({
  validationResult,
  sourceReferences = [],
  protocolId,
  showDetails = false
}) => {
  const [expanded, setExpanded] = useState(showDetails);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  // Получить информацию о протоколе
  const protocol = protocolId ? medicalVersionManager.getProtocol(protocolId) : null;
  const versionHistory = protocolId ? medicalVersionManager.getVersionHistory(protocolId) : null;

  return (
    <Card sx={{ 
      mb: 2, 
      border: '1px solid #E0E0E0',
      borderRadius: 2,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <CardContent sx={{ p: 2 }}>
        {/* Заголовок с кнопкой развертывания */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: expanded ? 2 : 0
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon sx={{ color: '#4A90E2', fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
              Медицинская информация
            </Typography>
            {validationResult && (
              <Badge 
                badgeContent={validationResult.errors.length + validationResult.warnings.length} 
                color={validationResult.severity === 'critical' ? 'error' : 'warning'}
              >
                <Chip
                  label={validationResult.severity}
                  size="small"
                  sx={{
                    backgroundColor: getSeverityColor(validationResult.severity),
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
              </Badge>
            )}
          </Box>
          <IconButton onClick={handleToggle} size="small">
            <ExpandMoreIcon sx={{ 
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease'
            }} />
          </IconButton>
        </Box>

        {/* Развернутое содержимое */}
        <Collapse in={expanded}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            
            {/* Результаты валидации */}
            {validationResult && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
                  🔍 Результаты проверки данных
                </Typography>
                
                {validationResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Ошибки валидации:
                    </Typography>
                    <List dense>
                      {validationResult.errors.map((error, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <ErrorIcon sx={{ color: '#F44336', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={error}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}

                {validationResult.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Предупреждения:
                    </Typography>
                    <List dense>
                      {validationResult.warnings.map((warning, index) => (
                        <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <WarningIcon sx={{ color: '#FF9800', fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={warning}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '0.875rem' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Alert>
                )}

                {validationResult.isValid && validationResult.warnings.length === 0 && (
                  <Alert severity="success">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Данные прошли валидацию успешно
                      </Typography>
                    </Box>
                  </Alert>
                )}
              </Box>
            )}

            {/* Источники данных */}
            {sourceReferences.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
                  📚 Источники данных
                </Typography>
                <List dense>
                  {sourceReferences.map((ref, index) => {
                    const source = sourceManager.getSource(ref.sourceId);
                    if (!source) return null;
                    
                    return (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <SourceIcon sx={{ color: sourceManager.getReliabilityColor(source.reliability), fontSize: 16 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {source.name}
                              </Typography>
                              <Chip
                                label={source.reliability}
                                size="small"
                                sx={{
                                  backgroundColor: sourceManager.getReliabilityColor(source.reliability),
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                              <Chip
                                label={sourceManager.getTypeIcon(source.type)}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#666' }}>
                              {source.authority} • {source.country} • Обновлено: {source.lastUpdated.toLocaleDateString('ru-RU')}
                            </Typography>
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            )}

            {/* Информация о протоколе */}
            {protocol && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
                  📋 Информация о протоколе
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#F8F9FA', 
                  borderRadius: 1,
                  border: '1px solid #E0E0E0'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {protocol.name}
                    </Typography>
                    <Chip
                      label={`v${protocol.version.fullVersion}`}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Последнее обновление: {protocol.lastUpdated.toLocaleDateString('ru-RU')}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                    Создан: {protocol.created.toLocaleDateString('ru-RU')}
                  </Typography>
                  
                  {versionHistory && versionHistory.versions.length > 1 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Всего версий: {versionHistory.versions.length}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Статистика источников */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#4A90E2' }}>
                📊 Статистика источников
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#F0F8FF', 
                borderRadius: 1,
                border: '1px solid #BBDEFB'
              }}>
                {(() => {
                  const stats = sourceManager.getSourcesStats();
                  return (
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Chip label={`Всего: ${stats.total}`} size="small" color="primary" />
                      <Chip label={`Высокая надежность: ${stats.byReliability.high || 0}`} size="small" />
                      <Chip label={`Устаревшие: ${stats.outdated}`} size="small" color={stats.outdated > 0 ? 'warning' : 'default'} />
                    </Box>
                  );
                })()}
              </Box>
            </Box>

            <Divider />
            
            {/* Предупреждение о медицинской ответственности */}
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                ⚠️ Медицинская ответственность
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                Данная информация предназначена только для справочных целей. 
                Все медицинские решения должны приниматься квалифицированным врачом 
                на основе клинической картины и дополнительных исследований.
              </Typography>
            </Alert>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MedicalInfo;
